// The languages the marketing site is published in, and how an address names
// one.
//
// English is the site as it always was, at the root, so no address that was
// published before the translations existed changes. Every other language
// lives under a prefix of its own — `/es/pricing`, `/pt-br/` — because a search
// engine indexes one language per URL and is told about the others by the
// alternate links each page carries ($lib/i18n/alternates). Nothing on this
// site picks a language for the visitor: no redirect by address or by the
// browser's language, only a switcher the visitor clicks.
//
// Simplified Chinese is addressed as `/zh/` and declared as `zh-Hans`: the
// address stays short and matches how the other codes are written, while the
// `lang` attribute and the alternate links carry the script, which is the part
// that tells a reader of Traditional Chinese this is not their text.

/** The code each language is addressed by, English first. */
export const LOCALES = [
  "en",
  "es",
  "pt-br",
  "id",
  "de",
  "fr",
  "vi",
  "ja",
  "ko",
  "zh",
  "tr",
  "ar",
  "it",
  "hi",
] as const;

export type Locale = (typeof LOCALES)[number];

/** Every language except the one at the root. */
export type TranslatedLocale = Exclude<Locale, "en">;

export const DEFAULT_LOCALE = "en" satisfies Locale;

export const TRANSLATED_LOCALES: readonly TranslatedLocale[] = LOCALES.filter(
  (locale): locale is TranslatedLocale => locale !== DEFAULT_LOCALE,
);

export interface LocaleInfo {
  /** The BCP 47 tag for `<html lang>` and for the alternate links. */
  readonly lang: string;
  /** Writing direction of the page. */
  readonly dir: "ltr" | "rtl";
  /** The language's name in itself, as the switcher lists it. */
  readonly nativeName: string;
  /** The Open Graph locale, language and region joined by an underscore. */
  readonly ogLocale: string;
}

export const LOCALE_INFO: Readonly<Record<Locale, LocaleInfo>> = {
  en: { lang: "en", dir: "ltr", nativeName: "English", ogLocale: "en_US" },
  es: { lang: "es", dir: "ltr", nativeName: "Español", ogLocale: "es_ES" },
  "pt-br": { lang: "pt-BR", dir: "ltr", nativeName: "Português (Brasil)", ogLocale: "pt_BR" },
  id: { lang: "id", dir: "ltr", nativeName: "Bahasa Indonesia", ogLocale: "id_ID" },
  de: { lang: "de", dir: "ltr", nativeName: "Deutsch", ogLocale: "de_DE" },
  fr: { lang: "fr", dir: "ltr", nativeName: "Français", ogLocale: "fr_FR" },
  vi: { lang: "vi", dir: "ltr", nativeName: "Tiếng Việt", ogLocale: "vi_VN" },
  ja: { lang: "ja", dir: "ltr", nativeName: "日本語", ogLocale: "ja_JP" },
  ko: { lang: "ko", dir: "ltr", nativeName: "한국어", ogLocale: "ko_KR" },
  zh: { lang: "zh-Hans", dir: "ltr", nativeName: "简体中文", ogLocale: "zh_CN" },
  tr: { lang: "tr", dir: "ltr", nativeName: "Türkçe", ogLocale: "tr_TR" },
  ar: { lang: "ar", dir: "rtl", nativeName: "العربية", ogLocale: "ar_AR" },
  it: { lang: "it", dir: "ltr", nativeName: "Italiano", ogLocale: "it_IT" },
  hi: { lang: "hi", dir: "ltr", nativeName: "हिन्दी", ogLocale: "hi_IN" },
};

/**
 * The languages whose pages ask to be indexed. Every other language is still
 * published, linked from the switcher and readable by anyone who picks it, but
 * each of its pages carries a robots `noindex, follow`, is left out of the
 * sitemap and of every page's alternate links, and is never announced to
 * IndexNow.
 *
 * Why a language is left out: its pages are translated without a native
 * speaker's review, and a young domain with few links that publishes many such
 * pages at once is exactly the pattern a search engine discounts as scaled
 * content — for the whole domain, not only for those pages. A language moves
 * into this list once searchers in its markets show up for the site, or once a
 * native speaker has reviewed its pages; nothing else about it has to change.
 */
export const INDEXED_LOCALES = ["en", "es", "pt-br", "id"] as const satisfies readonly Locale[];

/** Whether a language's pages ask to be indexed. */
export function isIndexedLocale(locale: Locale): boolean {
  return (INDEXED_LOCALES as readonly Locale[]).includes(locale);
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function isTranslatedLocale(value: string): value is TranslatedLocale {
  return value !== DEFAULT_LOCALE && isLocale(value);
}

/**
 * The address of a page in one language, from its English path.
 *
 * The English home is `/` and every other home is the bare prefix (`/es`),
 * which is the spelling the framework serves without a redirect.
 */
export function localePath(locale: Locale, path: string): string {
  if (locale === DEFAULT_LOCALE) return path;
  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

/** A request path taken apart into its language and its English path. */
export interface LocatedPath {
  readonly locale: Locale;
  readonly path: string;
}

/**
 * The language an address is in, read off its first segment, and the English
 * path behind it. Anything without a known prefix is English.
 */
export function splitLocale(pathname: string): LocatedPath {
  const match = /^\/([a-z]{2}(?:-[a-z]{2})?)(\/.*)?$/.exec(pathname);
  const candidate = match?.[1];
  if (candidate !== undefined && isTranslatedLocale(candidate)) {
    const rest = match?.[2] ?? "";
    return { locale: candidate, path: rest === "" || rest === "/" ? "/" : rest };
  }
  return { locale: DEFAULT_LOCALE, path: pathname };
}

/** Rewrites the opening `<html>` tag of a document for a language. */
export function setDocumentLanguage(html: string, locale: Locale): string {
  const { lang, dir } = LOCALE_INFO[locale];
  const attributes = dir === "rtl" ? `lang="${lang}" dir="rtl"` : `lang="${lang}"`;
  return html.replace('<html lang="en"', `<html ${attributes}`);
}
